/**
 * remarkSpineCitation — MDAST plugin that transforms
 * `[[spine:<persona_id>:<message_id>]]` tokens into custom AST nodes
 * with hProperties so ReactMarkdown's `components.span` mapping can
 * render them as <CitationPill>.
 *
 * Per spec §6.7 + cycle-2 frontend F5 + SP3-MF-04 (extended token
 * format with persona_id, so Reply Lens-snap can target the right
 * persona without round-tripping through the API).
 *
 * Runs BEFORE remarkGfm so the tokens are extracted before GFM tries
 * to interpret the [[...]] as some other construct.
 *
 * Implementation note: walks the tree by hand rather than via
 * unist-util-visit (which is ESM-only and incompatible with CRA's Jest
 * babel preset). The walker is short; not worth the test-runtime
 * cost to import the util.
 */


// Persona ids and message ids are uuids or short alphanumeric slugs.
// Allow letters/digits/hyphen + underscore.
const SPINE_CITATION_REGEX = /\[\[spine:([a-zA-Z0-9_-]+):([a-zA-Z0-9_-]+)\]\]/g;


function _walkAndTransform(node: any): void {
  if (!node || typeof node !== 'object') return;
  if (!Array.isArray(node.children)) return;

  const newChildren: any[] = [];
  for (const child of node.children) {
    if (child?.type === 'text' && typeof child.value === 'string') {
      const text: string = child.value;
      const matches = [...text.matchAll(SPINE_CITATION_REGEX)];
      if (matches.length === 0) {
        newChildren.push(child);
        continue;
      }
      let lastEnd = 0;
      for (const m of matches) {
        const start = m.index!;
        if (start > lastEnd) {
          newChildren.push({ type: 'text', value: text.slice(lastEnd, start) });
        }
        newChildren.push({
          type: 'spineCitation',
          personaId: m[1],
          spineId: m[2],
          data: {
            hName: 'span',
            hProperties: {
              'data-spine-citation-persona-id': m[1],
              'data-spine-citation-spine-id': m[2],
              className: 'spine-citation-marker',
            },
          },
        });
        lastEnd = start + m[0].length;
      }
      if (lastEnd < text.length) {
        newChildren.push({ type: 'text', value: text.slice(lastEnd) });
      }
    } else {
      _walkAndTransform(child);
      newChildren.push(child);
    }
  }
  node.children = newChildren;
}


export function remarkSpineCitation() {
  return (tree: any) => {
    _walkAndTransform(tree);
  };
}
